<template>
  <SettingsLayout>
    <div class="settings-content">
      <div class="heading">
        <h1 class="h2">Connected apps</h1>
      </div>
      <div class="block">
        <div class="mxw">
          <h2 class="h3">Apps connected to your account</h2>

          <!-- Loading -->
          <p v-if="loading">Loading connected apps...</p>

          <!-- The list could not be loaded -->
          <template v-else-if="loadError">
            <p class="connected-apps__error" role="alert">{{ loadError }}</p>
            <p>
              <a href="#" @click.prevent="loadConnections">Try again</a>
            </p>
          </template>

          <!-- One or more apps connected -->
          <template v-else-if="connections.length">
            <p>
              These apps have access to your AudioScribe account. You can
              disconnect any of them below.
            </p>
            <!--
              While the confirmation is open the whole list is inert, so a
              background row can't be reached by keyboard and silently
              re-target the dialog. The per-button `disabled` below is the
              belt-and-braces version of the same rule.
            -->
            <div class="connected-apps__list" :inert="isConfirming || null">
              <div
                v-for="app in connections"
                :key="app.connection"
                class="connected-app"
              >
                <div class="connected-app__row">
                  <div class="connected-app__identity">
                    <h3 class="connected-app__name">{{ nameFor(app) }}</h3>
                    <!--
                      The app's own name is chosen by whoever registered it, so
                      it is not proof of identity. The client id is, and it is
                      shown here so "AudioScribe Official" cannot pass itself
                      off as us. Bound as text; the server also strips bidi and
                      zero-width characters from both fields.
                    -->
                    <p class="connected-app__id">{{ app.displayId }}</p>
                  </div>
                  <button
                    type="button"
                    class="btn btn-secondary-outline connected-app__action"
                    :aria-label="`Disconnect ${nameFor(app)}`"
                    :disabled="isBusy || isConfirming"
                    @click="askToDisconnect(app)"
                  >
                    Disconnect
                  </button>
                </div>
                <ul
                  v-if="app.scopes && app.scopes.length"
                  class="connected-app__scopes"
                >
                  <li v-for="scope in app.scopes" :key="scope">
                    {{ scopeLabel(scope) }}
                  </li>
                </ul>
                <p
                  v-if="disconnectError.connection === app.connection"
                  class="connected-apps__error"
                  role="alert"
                >
                  {{ disconnectError.message }}
                </p>
              </div>
            </div>
          </template>

          <!-- Nothing connected — the common case -->
          <template v-else>
            <p>You haven't connected any apps yet.</p>
            <p>
              Connecting an AI assistant — Claude, or any other tool that speaks
              MCP — lets you ask it about your AudioScribe recordings. It reads
              the transcripts it needs straight from your account, so you don't
              have to copy anything across. Anything you connect shows up here,
              and you can disconnect it whenever you like.
            </p>
            <p>
              <a
                :href="helpUrl"
                target="_blank"
                rel="noopener"
                @click="trackHelpClick"
                >How to connect Claude</a
              >
            </p>
          </template>
        </div>
      </div>
    </div>

    <ConfirmDialog
      :show="!!pendingApp"
      :title="confirmTitle"
      confirm-text="Disconnect"
      cancel-text="Cancel"
      variant="danger"
      :is-loading="isBusy"
      @confirm="disconnect"
      @cancel="cancelDisconnect"
    >
      <!--
        Separate ELEMENTS, not one newline-joined string. A joined string needs
        white-space: pre-line to show its lines, and that renders the
        attacker's newlines too — a name of "Claude\nClient ID: tpc_official"
        would forge a labelled id line above the real one. Distinct elements
        make that structurally impossible: a name can never become a sibling.
      -->
      <div class="confirm-identity">
        <p class="confirm-identity__field">
          <span class="confirm-identity__label">App</span>
          <span class="confirm-identity__value">{{ confirmName }}</span>
        </p>
        <p class="confirm-identity__field">
          <span class="confirm-identity__label">Client ID</span>
          <span class="confirm-identity__value confirm-identity__value--mono">{{
            confirmId
          }}</span>
        </p>
      </div>
      <p class="confirm-message">{{ confirmBody }}</p>
    </ConfirmDialog>
  </SettingsLayout>
</template>

<script>
import SettingsLayout from "@/components/SettingsLayout.vue";
import ConfirmDialog from "@/components/popups/ConfirmDialog.vue";
import {
  getMcpConnections,
  disconnectMcpConnection,
} from "@/services/mcp-connections.service";
import { analytics } from "@/services/analytics.service";

// Help guide lives on the marketing site, not in the app.
const HELP_URL = "https://audioscribe.io/help/connect-claude-mcp";

// Reviewed copy — revocation is NOT instant and the wording must not imply it.
// Client-neutral: any MCP client can appear here, so the message must not
// assume Claude. Identity is rendered as separate labelled elements above it.
// Fixed copy. The app's identity is presented as LABELLED FIELDS above it
// rather than interpolated into the sentence: a name is attacker-chosen, and a
// name like "Foo (tpc_realid) — press Cancel to keep" reads as dialog wording
// and can appear before the genuine id. Keeping the prose fixed means nothing
// a client chooses can imitate it.
const CONFIRM_BODY =
  "This app will no longer be able to request new access to your AudioScribe " +
  "account. An access token it already has may keep working for up to an " +
  "hour. Anything it already retrieved stays wherever it was sent.";

// Shorter than the server's 200 cap: the dialog is the last checkpoint before
// an irreversible action, so the NAME must stay glanceable.
//
// Applies to the name ONLY. The client id is never truncated — see safeId.
const CONFIRM_NAME_MAX = 60;
// Local mirror of the server's sanitizer. The server already does this, and
// this deliberately repeats it: a view must not depend on a remote sanitizer
// for how it renders a security decision. Same two rules — Unicode invisibles
// and formatting characters by property, plus the line separators that are not
// covered by it.
const stripInvisible = (value) =>
  String(value ?? "")
    .replace(/[\u0000-\u001F\u007F-\u009F\u2028\u2029]/gu, " ")
    .replace(/[\p{Default_Ignorable_Code_Point}\p{Cf}]/gu, "")
    .replace(/\s+/g, " ")
    .trim();

/** The NAME: attacker-chosen, so clipped to stay glanceable. */
const clip = (value) => {
  const flat = stripInvisible(value);
  return flat.length > CONFIRM_NAME_MAX
    ? `${flat.slice(0, CONFIRM_NAME_MAX - 1)}…`
    : flat;
};

/**
 * The CLIENT ID: never truncated.
 *
 * It is the one value a client cannot choose for itself, which is the entire
 * reason the dialog shows it. Truncating it hands that back: two hostile ids
 * sharing a 59-character prefix would render identically at the last
 * checkpoint before an irreversible action. It wraps instead.
 */
const safeId = (value) => stripInvisible(value);

// Granted scopes are shown in plain language, never as raw scope strings.
const SCOPE_LABELS = {
  "transcripts:read": "Read your transcripts",
  "workspaces:read": "List your workspaces",
  // Not an AudioScribe permission and not access to any data — the client
  // asks for it so it can renew its own session. Named here because the
  // fallback below would otherwise render "Read your offline access", which
  // claims a read this scope does not grant.
  offline_access: "Stay connected without signing in again",
};

const NO_ERROR = { connection: null, message: "" };

const isNonEmptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

const isPlainObject = (value) =>
  !!value && typeof value === "object" && !Array.isArray(value);

/**
 * A connection we are prepared to render. This page is the only place a user
 * can see who holds access to their account, so an entry we don't fully
 * recognise is treated as a failure to load the list — never as a shorter
 * list, and never as "nothing is connected".
 */
const isValidConnection = (entry) =>
  isPlainObject(entry) &&
  isNonEmptyString(entry.connection) &&
  isNonEmptyString(entry.displayName) &&
  // displayId is required, not optional. It is the part of a row a client
  // cannot choose for itself, so a row without it cannot be told apart from
  // one impersonating another app by name.
  isNonEmptyString(entry.displayId) &&
  Array.isArray(entry.scopes) &&
  entry.scopes.every(isNonEmptyString);

/**
 * Proof that the DELETE did what we asked. A 2xx on its own is not proof: the
 * body must name this exact connection and state that it was disconnected.
 * Anything else (empty body, `{}`, `disconnected: false`, another app's id)
 * is a failure, because telling someone access is revoked when it isn't is
 * the worst thing this page can do.
 */
const isDisconnectConfirmed = (data, connection) =>
  isPlainObject(data) &&
  data.disconnected === true &&
  isNonEmptyString(data.connection) &&
  data.connection === connection;

export default {
  name: "SettingsConnectedAppsView",
  components: {
    SettingsLayout,
    ConfirmDialog,
  },
  data() {
    return {
      connections: [],
      loading: true,
      loadError: "",
      // The app awaiting confirmation. Non-null == the dialog is open.
      pendingApp: null,
      // True while a disconnect is in flight — blocks every entry point.
      isBusy: false,
      disconnectError: { ...NO_ERROR },
      helpUrl: HELP_URL,
    };
  },
  computed: {
    // The dialog is open, so its target is frozen until it closes.
    isConfirming() {
      return !!this.pendingApp;
    },
    // No attacker-chosen text in the title at all.
    confirmTitle() {
      return "Disconnect this app?";
    },
    confirmName() {
      return this.pendingApp ? clip(this.nameFor(this.pendingApp)) : "";
    },
    confirmId() {
      return this.pendingApp ? safeId(this.pendingApp.displayId) : "";
    },
    confirmBody() {
      return this.pendingApp ? CONFIRM_BODY : "";
    },
  },
  mounted() {
    analytics.settingsConnectedAppsViewed();
    this.loadConnections();
  },
  methods: {
    nameFor(app) {
      // Never falls back to `connection` — that is the opaque base64 handle,
      // which is meaningless to a user. displayId is the readable identifier.
      return app?.displayName || app?.displayId || "Connected app";
    },
    scopeLabel(scope) {
      if (SCOPE_LABELS[scope]) return SCOPE_LABELS[scope];

      // Unknown scope: still render something readable rather than the raw
      // string, so a new backend scope never leaks jargon into the UI.
      const [resource, action] = String(scope || "").split(":");
      const subject = resource.replace(/[_-]+/g, " ").trim();
      if (!subject) return "Access your AudioScribe account";
      // Only claim "Read" when the scope actually says read. A bare scope with
      // no action (offline_access was the first) grants something other than
      // reading, so describing it as a read overstates what the user allowed.
      if (action === "read") return `Read your ${subject}`;
      if (action === "write") return `Change your ${subject}`;
      return `Access your ${subject}`;
    },
    async loadConnections() {
      this.loading = true;
      this.loadError = "";

      try {
        const { data, error } = await getMcpConnections();

        if (error) {
          this.loadError = this.loadErrorMessage(error.status);
          this.connections = [];
          return;
        }

        // A response we can't read is a failed load, not an empty account.
        // `{"connections": []}` is the genuine empty case and still renders
        // the empty state.
        const list = isPlainObject(data) ? data.connections : undefined;
        if (!Array.isArray(list) || !list.every(isValidConnection)) {
          this.loadError = this.loadErrorMessage();
          this.connections = [];
          return;
        }

        this.connections = list;
      } catch (e) {
        this.loadError = this.loadErrorMessage();
        this.connections = [];
      } finally {
        this.loading = false;
      }
    },
    askToDisconnect(app) {
      // A second click while a disconnect is running must not queue another,
      // and once a confirmation is open its target is frozen: reaching another
      // row (keyboard, stale focus) must never swap the app under the dialog.
      if (this.isBusy || this.pendingApp) return;

      this.disconnectError = { ...NO_ERROR };
      this.pendingApp = app;
      analytics.connectedAppDisconnectClicked({ connection: app.connection });
    },
    cancelDisconnect() {
      if (this.isBusy) return;
      this.pendingApp = null;
    },
    async disconnect() {
      // Only ever reachable from the confirmation dialog, and only once at a
      // time — double-clicking Disconnect cannot skip or repeat this.
      if (this.isBusy || !this.pendingApp) return;

      const app = this.pendingApp;
      this.isBusy = true;
      this.disconnectError = { ...NO_ERROR };

      try {
        const { data, error } = await disconnectMcpConnection(app.connection);

        if (error) {
          this.failDisconnect(app, error.status);
          return;
        }

        // Validate before any success side effect: no dialog close, no toast,
        // no list refresh until the server has confirmed this exact app.
        if (!isDisconnectConfirmed(data, app.connection)) {
          this.failDisconnect(app, undefined, "invalid_response");
          return;
        }

        this.pendingApp = null;
        analytics.connectedAppDisconnected({ connection: app.connection });
        this.$toast.success("Disconnected.");
        await this.loadConnections();
      } catch (e) {
        this.failDisconnect(app);
      } finally {
        this.isBusy = false;
      }
    },
    // A failed disconnect must never read as a success: the row stays, the
    // list is not refreshed, and the reason is shown next to the app.
    failDisconnect(app, status, reason) {
      const message = this.disconnectErrorMessage(status);
      this.pendingApp = null;
      this.disconnectError = { connection: app.connection, message };
      analytics.connectedAppDisconnectFailed({
        connection: app.connection,
        status: status || reason || "network_error",
      });
      this.$toast.error(message);
    },
    loadErrorMessage(status) {
      if (status === 429) {
        return "Too many requests just now. Wait a minute, then try again.";
      }
      if (status === 503) {
        return "We couldn't reach the service that manages app access. Please try again in a few minutes.";
      }
      if (status === 401) {
        return "Your session has expired. Please sign in again.";
      }
      return "We couldn't load your connected apps. Please try again.";
    },
    disconnectErrorMessage(status) {
      if (status === 429) {
        return "Too many requests just now, so nothing was disconnected. Wait a minute, then try again.";
      }
      if (status === 503) {
        return "We couldn't reach the service that manages app access, so nothing was disconnected. Please try again in a few minutes.";
      }
      if (status === 401) {
        return "Your session has expired, so nothing was disconnected. Please sign in again.";
      }
      if (status === 400) {
        return "We don't recognize that app, so nothing was disconnected.";
      }
      // No status means the request never came back cleanly, or came back with
      // a body we can't trust. The server may or may not have processed it, so
      // this must not claim either outcome. Disconnecting twice is harmless.
      return "We couldn't confirm whether this app was disconnected. It's safe to try again.";
    },
    trackHelpClick() {
      analytics.connectedAppsHelpClicked();
    },
  },
};
</script>

<style scoped>
.connected-app {
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid #e6e6e6;
}

.connected-app:last-child {
  padding-bottom: 0;
  margin-bottom: 0;
  border-bottom: 0;
}

.connected-app__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.connected-app__identity {
  min-width: 0;
}

.confirm-identity {
  margin: 0 0 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.confirm-identity__field {
  margin: 0;
  display: flex;
  gap: 8px;
  font-size: 13px;
  min-width: 0;
}

.confirm-identity__label {
  flex: 0 0 70px;
  color: var(--text-secondary, #6b7280);
}

/* Clipped here too: the dialog is the last checkpoint before an irreversible
   action, so neither field may push the other out of view. */
.confirm-identity__value {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}

/* The id is shown in FULL and wraps. No ellipsis: an elided id cannot be
   compared, and comparing it is why it is here. */
.confirm-identity__value--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-weight: 400;
  white-space: normal;
  overflow: visible;
  overflow-wrap: anywhere;
  text-overflow: clip;
}

.connected-app__name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  /* A name is attacker-chosen, so it is CLIPPED to one line — a 200-char name
     must not push the authoritative client id below the fold or shove the
     Disconnect button off screen.
     An earlier version used `overflow-wrap: anywhere` while the comment
     claimed clipping; that property does the opposite and permits wrapping. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.connected-app__id {
  margin: 2px 0 0;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: var(--text-secondary, #6b7280);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.connected-app__action {
  flex-shrink: 0;
  cursor: pointer;
}

.connected-app__action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.connected-app__scopes {
  margin: 10px 0 0;
  padding-left: 18px;
  font-size: 14px;
  color: #4a4a4a;
}

.connected-app__scopes li {
  margin-bottom: 4px;
}

.connected-apps__error {
  color: #d1382c;
  font-size: 14px;
}
</style>
